/**
 * fastTick — single shared 250ms ticker for decay animations.
 *
 * Same pattern as `useNow` (one module-level setInterval feeding every
 * subscriber, lazy start/stop), but at 4 Hz for the ~10s consensus
 * glow/arrow fades where a 1s step is visibly chunky. Deliberately NOT a
 * hook: consumers subscribe conditionally (only while their value is still
 * decaying) so an idle dashboard doesn't re-render 4×/s — see
 * `useConsensusGlow`.
 */

export const FAST_TICK_MS = 250;

const _listeners = new Set<() => void>();
let _intervalId: ReturnType<typeof setInterval> | null = null;

function tick(): void {
  for (const l of _listeners) l();
}

/** Subscribe to the shared 4 Hz tick. Returns the unsubscribe. The interval
 *  only runs while at least one subscriber is attached. */
export function subscribeFastTick(cb: () => void): () => void {
  _listeners.add(cb);
  if (_intervalId == null) {
    _intervalId = setInterval(tick, FAST_TICK_MS);
  }
  return () => {
    _listeners.delete(cb);
    if (_intervalId != null && _listeners.size === 0) {
      clearInterval(_intervalId);
      _intervalId = null;
    }
  };
}
